import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { reportEvents, type AnalyticsEventPayload } from '@/api/analytics';

type GtagCommand = 'js' | 'config' | 'set' | 'event';

type Gtag = (
  command: GtagCommand,
  target: string | Date,
  params?: Record<string, unknown>
) => void;

type AnalyticsUserProperties = {
  tenant_uuid?: string;
  workspace_uuid?: string;
  email?: string;
  nickname?: string;
  full_name?: string;
  role?: string;
  [key: string]: unknown;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Gtag;
    ga4Initialized?: boolean;
    ga4ClickTrackingBound?: boolean;
  }
}

const CLICK_SELECTOR =
  'a, button, [role="button"], [data-analytics-event], [data-analytics-id]';
const MAX_TEXT_LENGTH = 120;
const MAX_CLASS_LENGTH = 200;
const BUFFER_FLUSH_INTERVAL = 5000;
const BUFFER_MAX_SIZE = 20;
const PARAMS_JSON_MAX_LENGTH = 4096;

let lastTrackedPagePath = '';
let currentUserId: string | undefined;
let currentTenantUuid: string | undefined;
let currentWorkspaceUuid: string | undefined;
let currentUserProperties: AnalyticsUserProperties = {};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let flushTimer: ReturnType<typeof setInterval> | null = null;

const eventBuffer: AnalyticsEventPayload[] = [];

const getMeasurementId = () => import.meta.env.VITE_GA4_MEASUREMENT_ID?.trim();

const isGa4Enabled = () => {
  return typeof window !== 'undefined' && !!getMeasurementId();
};

/** 后端上报独立于 GA4，始终启用 */
const isBackendReportingEnabled = () => {
  return typeof window !== 'undefined';
};

/** 检测 GA4 gtag.js 脚本是否已成功加载 */
const isGtagScriptLoaded = (): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof (window as any).google_tag_manager !== 'undefined';
};

const cleanParams = (params?: Record<string, unknown>) => {
  if (!params) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null
    )
  );
};

const ensureGtag = () => {
  if (!isGa4Enabled()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    } as Gtag;
  }
};

const sendGtag = (
  command: GtagCommand,
  target: string | Date,
  params?: Record<string, unknown>
) => {
  if (!isGa4Enabled()) {
    return;
  }

  ensureGtag();
  window.gtag?.(command, target, cleanParams(params));
};

const normalizePagePath = (path: string) => {
  return path.replace(/^\/space\/[^/]+/, '/space/:workspace_uuid');
};

const truncateText = (value?: string, maxLength = MAX_TEXT_LENGTH) => {
  if (!value) {
    return undefined;
  }

  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

const normalizeWhitespace = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized || undefined;
};

const getElementText = (element: HTMLElement) => {
  return truncateText(
    normalizeWhitespace(
      element.dataset.analyticsLabel ||
        element.getAttribute('aria-label') ||
        element.getAttribute('title') ||
        element.innerText ||
        element.textContent
    )
  );
};

const getElementClass = (element: HTMLElement) => {
  return truncateText(
    normalizeWhitespace(element.className || undefined),
    MAX_CLASS_LENGTH
  );
};

const getLinkMetadata = (element: HTMLElement) => {
  const anchor = element.closest('a');

  if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) {
    return {};
  }

  try {
    const url = new URL(anchor.href, window.location.origin);
    const isExternal = url.origin !== window.location.origin;

    return {
      href: isExternal
        ? url.origin + url.pathname
        : normalizePagePath(url.pathname),
      is_external: isExternal,
    };
  } catch {
    return {};
  }
};

const getClickTarget = (event: MouseEvent) => {
  if (!(event.target instanceof Element)) {
    return null;
  }

  const target = event.target.closest(CLICK_SELECTOR);

  if (!(target instanceof HTMLElement)) {
    return null;
  }

  if (target.dataset.analyticsIgnore === 'true') {
    return null;
  }

  if (
    target instanceof HTMLButtonElement ||
    target.getAttribute('role') === 'button'
  ) {
    if (
      target.hasAttribute('disabled') ||
      target.getAttribute('aria-disabled') === 'true'
    ) {
      return null;
    }
  }

  return target;
};

// --- 自有后端上报 ---

const ANONYMOUS_ID_KEY = 'knit_anonymous_id';
const SESSION_ID_KEY = 'knit_session_id';
const UTM_STORAGE_KEY = 'knit_utm';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const getAnonymousId = (): string => {
  let id = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (!id) {
    id = `anon_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
    localStorage.setItem(ANONYMOUS_ID_KEY, id);
  }
  return id;
};

const getSessionId = (): string => {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = `sess_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
};

const captureUTM = () => {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });

  if (Object.keys(utm).length > 0) {
    utm.landing_page = window.location.pathname;
    utm.referrer = document.referrer;
    utm.captured_at = new Date().toISOString();
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
  }
};

export const getUTMParams = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(UTM_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const BACKEND_KNOWN_FIELDS = ['page_path', 'page_title', 'page_name'] as const;

const buildBackendEvent = (
  eventName: string,
  params?: Record<string, unknown>
): AnalyticsEventPayload => {
  const cleaned = cleanParams(params) || {};

  const knownFields: Partial<AnalyticsEventPayload> = {};
  const extraParams: Record<string, unknown> = {};

  Object.entries(cleaned).forEach(([key, value]) => {
    if ((BACKEND_KNOWN_FIELDS as readonly string[]).includes(key)) {
      (knownFields as Record<string, unknown>)[key] = value;
    } else {
      extraParams[key] = value;
    }
  });

  const fullParams: Record<string, unknown> = {
    ...extraParams,
    ...getUTMParams(),
    user_id: currentUserId,
    user_email: currentUserProperties.email,
    user_nickname: currentUserProperties.nickname,
    user_full_name: currentUserProperties.full_name,
    user_role: currentUserProperties.role,
  };
  const paramsJson = JSON.stringify(fullParams);

  return {
    event_name: eventName,
    event_time: new Date().toISOString(),
    source: 'frontend_ga4',
    anonymous_id: getAnonymousId(),
    session_id: getSessionId(),
    tenant_uuid: currentTenantUuid,
    workspace_uuid: currentWorkspaceUuid,
    params_json:
      paramsJson.length > PARAMS_JSON_MAX_LENGTH
        ? paramsJson.slice(0, PARAMS_JSON_MAX_LENGTH)
        : paramsJson,
    ga4_client_active: isGtagScriptLoaded(),
    env: import.meta.env.MODE || 'unknown',
    ...knownFields,
  };
};

const flushBuffer = () => {
  if (eventBuffer.length === 0) {
    return;
  }

  const events = eventBuffer.splice(0);
  reportEvents(events).catch(() => {
    // 静默忽略，不影响用户体验
  });
};

const flushBufferWithBeacon = () => {
  if (eventBuffer.length === 0) {
    return;
  }

  const events = eventBuffer.splice(0);
  const body = JSON.stringify({ events });

  try {
    navigator.sendBeacon(
      '/api/console/analytics/v1/events',
      new Blob([body], { type: 'application/json' })
    );
  } catch {
    // 静默忽略
  }
};

const pushToBuffer = (event: AnalyticsEventPayload) => {
  eventBuffer.push(event);
  if (eventBuffer.length >= BUFFER_MAX_SIZE) {
    flushBuffer();
  }
};

// --- 对外接口 ---

export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
) => {
  sendGtag('event', eventName, params);
  pushToBuffer(buildBackendEvent(eventName, params));
};

const bindAutoClickTracking = () => {
  if (!isBackendReportingEnabled() || window.ga4ClickTrackingBound) {
    return;
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = getClickTarget(event);

      if (!target) {
        return;
      }

      const isLink = !!target.closest('a');
      const eventName =
        target.dataset.analyticsEvent ||
        (isLink ? 'click_link' : 'click_button');

      trackEvent(eventName, {
        page_path: normalizePagePath(window.location.pathname),
        page_title: document.title || undefined,
        element_id: target.dataset.analyticsId || target.id || undefined,
        element_text: getElementText(target),
        element_class: getElementClass(target),
        element_tag: target.tagName.toLowerCase(),
        ...getLinkMetadata(target),
      });
    },
    true
  );

  window.ga4ClickTrackingBound = true;
};

export const initAnalytics = () => {
  if (window.ga4Initialized) {
    return;
  }

  // GA4 初始化（仅在配置了 measurement ID 时）
  if (isGa4Enabled()) {
    ensureGtag();
  }

  captureUTM();

  // 自动点击追踪和缓冲区刷新：无论 GA4 是否启用都需要工作（后端上报独立通道）
  bindAutoClickTracking();

  flushTimer = setInterval(flushBuffer, BUFFER_FLUSH_INTERVAL);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushBufferWithBeacon();
    }
  });
  window.addEventListener('pagehide', flushBufferWithBeacon);

  window.ga4Initialized = true;
};

export const trackPageView = (route: RouteLocationNormalizedLoaded) => {
  if (!isBackendReportingEnabled()) {
    return;
  }

  const normalizedPath = normalizePagePath(route.path);

  if (normalizedPath === lastTrackedPagePath) {
    return;
  }

  lastTrackedPagePath = normalizedPath;

  trackEvent('page_view', {
    page_title: document.title || String(route.name || ''),
    page_path: normalizedPath,
    page_location: `${window.location.origin}${normalizedPath}`,
    page_name: route.name ? String(route.name) : undefined,
    ...getUTMParams(),
  });
};

export const syncAnalyticsUser = (
  userId?: string,
  properties?: AnalyticsUserProperties
) => {
  // 始终同步用户上下文（后端上报需要）
  currentUserId = userId;
  currentTenantUuid = properties?.tenant_uuid;
  currentWorkspaceUuid = properties?.workspace_uuid;
  currentUserProperties = properties || {};

  // GA4 用户属性同步（仅在 GA4 启用时）
  const measurementId = getMeasurementId();
  if (measurementId) {
    sendGtag('config', measurementId, {
      send_page_view: false,
      user_id: userId || undefined,
    });

    sendGtag('set', 'user_properties', {
      ...properties,
      anonymous_id: getAnonymousId(),
    });
  }
};

export const clearAnalyticsUser = () => {
  flushBuffer();
  syncAnalyticsUser(undefined, {});
  lastTrackedPagePath = '';
};

/* eslint-disable no-console, no-underscore-dangle */
/**
 * 调试工具：发送测试事件并立即 flush，用于验证后端 GA4 MP 转发是否正常
 * 浏览器控制台调用: window.analyticsDebug.sendTestEvent()
 */
if (import.meta.env.DEV || import.meta.env.VITE_SENTRY_ENV !== 'production') {
  (window as any).analyticsDebug = {
    sendTestEvent: (eventName = 'debug_test_event') => {
      const event = buildBackendEvent(eventName, {
        page_path: window.location.pathname,
        page_title: document.title,
        debug: true,
        test_timestamp: new Date().toISOString(),
      });
      console.log('[Analytics Debug] 发送测试事件:', event);
      reportEvents([event])
        .then((res) => {
          console.log('[Analytics Debug] 后端响应:', res.data);
          console.log(
            '[Analytics Debug] 请在 GA4 实时报告中查看事件:',
            eventName
          );
        })
        .catch((err) => {
          console.error('[Analytics Debug] 上报失败:', err);
        });
      return event;
    },
    getBufferState: () => ({
      bufferSize: eventBuffer.length,
      events: [...eventBuffer],
    }),
    flushNow: () => {
      console.log(
        '[Analytics Debug] 手动 flush, 当前 buffer 大小:',
        eventBuffer.length
      );
      flushBuffer();
    },
  };
}
/* eslint-enable no-console, no-underscore-dangle */
